import { useEffect, useState, useCallback } from "react";
import {
  Box,
  Divider,
  IconButton,
  LinearProgress,
  Menu,
  MenuItem,
  Paper,
  Tooltip,
} from "@mui/material";
import { ContentCut, FilterAlt, Tune } from "@mui/icons-material";
import {
  ASIFilterDialog,
  ExtractDialog,
  EditorDAWFineTuneDialog,
  NLMSFilterDialog,
  SingleChannelActiveDialog,
  SngSettingDialogNEW,
  SyncDialog,
  SyncSelectedDialog,
} from "../../dialogs";
import { useAlerts } from "../../../store/alerts/hooks";
import { useAnnotations } from "../../../store/annotations/hooks";
import { useChannels } from "../../../store/channels/hooks";
import {
  useEditor,
  useEditorPreferences,
  useEditorSelection,
} from "../../../store/editor/hooks";
import { useFeature } from "flagged";
import { SYNC } from "../../../FeatureFlags";
import { useModals } from "../../../store/dialogs/hooks";
import { loadCase } from "../../../pages/app/utils/dataLoader";
import { GlimpseAlert, SngAlert, SyncAlert } from "../../alerts";
import { ASI, NLMS } from "../../../pages/app/utils/constants";
import { EditorDAWTimeDisplay } from "./EditorDAWTimeDisplay";

export const EditorDAWEditorMenu = ({
  activeRegionGroup,
  caseId,
  clipLength,
  currentTime,
  daw,
  duration,
  isNoiseFiltering,
  mode,
  numberOfChannels,
  onAsiFilter,
  onCutRegion,
  onGlimpseFilter,
  onMergeRegion,
  onNlmsFilter,
  onNormalizeFilter,
  onSngFilter,
  onSyncFilter,
  readOnly,
  sampleRate,
  spectrogramVariant,
  surferLoading,
  syncSelected,
  trackId,
  working,
}) => {
  const useSync = useFeature(SYNC);
  const { openGlimpseAlert, openSngAlert, openSyncAlert } = useAlerts();
  const { annotations, isSaving } = useAnnotations();
  const { isNotMonoActiveChannel } = useChannels();
  const { selectedTag, isFiltering } = useEditor();
  const { preferences, updateFFTFrequencies, updatePreferences } =
    useEditorPreferences();
  const { selectTag } = useEditorSelection();
  const { fftFrequencies } = preferences;
  const {
    closeEditorDAWFineTuneDialog,
    openASIFilterDialog,
    openNLMSFilterDialog,
    openExtractDialog,
    openSingleChannelActiveDialog,
    openSngFilterDialogNEW,
    openSyncDialog,
    openEditorDAWFineTuneDialog,
    closeSyncSelectedDialog,
    openSyncSelectedDialog,
  } = useModals();
  const regions = annotations;
  const [anchorEl, setAnchorEl] = useState(null);
  const [currentCase, setCurrentCase] = useState({});
  const [loaded, setLoaded] = useState(false);
  const [filterName, setFilterName] = useState("");
  const [fftSizeOptions, setFftSizeOptions] = useState([]);
  const [fftOptionsDefault, setFftOptionsDefault] = useState(null);
  const hasGlimpseOptions = loaded && currentCase?.numberOfClips > 1;
  const open = Boolean(anchorEl);
  const isLimited = mode === "sng" || mode === "sync"; // limited = no annotation, no cut, no merge
  const { name: activeRegionGroupName } = activeRegionGroup || {};
  const canPerformOperationsOnSelection =
    !isFiltering && activeRegionGroup?.regions?.length > 0;
  const handleSyncSelectedClose = () => closeSyncSelectedDialog();
  const handleSyncSelectedContinue = () => {
    closeSyncSelectedDialog();
    showSyncDialog();
  };

  const load = useCallback(async () => {
    const currCase = await loadCase(caseId);
    setCurrentCase(currCase);
    const freqMax = Math.min(fftFrequencies.max, sampleRate / 2.0); // if there are no preferences, it defaults to 20000 which may be way higher than the possible range given the sample rate
    if (trackId && daw) {
      const options = daw
        ?.getSpectrogramVariants(trackId)
        .filter((item) => item.fftSize >= 64);
      setFftSizeOptions(options);
      setFftOptionsDefault(options.find((item) => item.isDefault) || null);
    }
    if (freqMax < fftFrequencies.max && daw && trackId) {
      updateFFTFrequencies({
        min: fftFrequencies.min,
        max: freqMax,
      });
      daw?.setTrackFrequencyRange(trackId, fftFrequencies.min, freqMax);
    }
    setLoaded(true);
  }, [
    caseId,
    daw,
    fftFrequencies.max,
    fftFrequencies.min,
    sampleRate,
    trackId,
    updateFFTFrequencies,
  ]);

  const handleMenuClick = (event) => setAnchorEl(event.currentTarget);

  const handleMenuClose = () => setAnchorEl(null);

  const handleGlimpseClick = () =>
    hasGlimpseOptions
      ? onGlimpseFilter()
      : (handleMenuClose(), openGlimpseAlert());

  const handleSyncClick = () => {
    if (
      regions?.map((item) => item.tag).filter((item) => item === selectedTag)
        .length > 1
    ) {
      handleMenuClose();
      openSyncAlert();
    } else if (syncSelected) {
      handleMenuClose();
      openSyncSelectedDialog();
    } else {
      showSyncDialog();
    }
  };

  const syncDisabled =
    !canPerformOperationsOnSelection ||
    numberOfChannels < 2 ||
    !isNotMonoActiveChannel;

  const sngDisabled =
    !canPerformOperationsOnSelection ||
    (numberOfChannels !== 1 && isNotMonoActiveChannel);

  const monoFilterDisabled = numberOfChannels > 1 && isNotMonoActiveChannel;
  const createModelDisabled = isFiltering || regions.length < 1;
  const glimpseDisabled =
    numberOfChannels < 2 || isLimited || !isNotMonoActiveChannel;

  const mergeRegion = (tag, comment) => {
    if (onMergeRegion) {
      onMergeRegion(tag, comment);
    }
  };
  const cutRegion = (selectedTag) => onCutRegion(selectedTag);

  const handleFineTuneCancel = () => closeEditorDAWFineTuneDialog();

  const handleTrimClick = () => cutRegion(activeRegionGroupName);

  const runSngFilter = () => onSngFilter();

  const runAsiFilter = (formValues) => onAsiFilter(formValues);

  const handleAsiClick = () => {
    handleMenuClose();

    if (numberOfChannels === 1) {
      openASIFilterDialog();
    } else if (numberOfChannels > 1 && !isNotMonoActiveChannel) {
      setFilterName(ASI);
      openSingleChannelActiveDialog();
    }
  };

  const runNlmsFilter = (formValues) => onNlmsFilter(formValues);

  const handleNlmsClick = () => {
    handleMenuClose();

    if (numberOfChannels === 1) {
      openNLMSFilterDialog();
    } else if (numberOfChannels > 1 && !isNotMonoActiveChannel) {
      setFilterName(NLMS);
      openSingleChannelActiveDialog();
    }
  };

  const normalizeFilter = () => {
    handleMenuClose();
    onNormalizeFilter();
  };

  const handleCreateModel = () => {
    handleMenuClose();
    openExtractDialog();
  };

  const handleSngShowDialogNEWClick = () => {
    if (
      regions?.map((item) => item.tag).filter((item) => item === selectedTag)
        .length > 1
    ) {
      handleMenuClose();
      openSngAlert();
    } else {
      handleMenuClose();
      openSngFilterDialogNEW();
    }
  };

  const runSync = (formValues) => onSyncFilter(formValues);

  const showSyncDialog = () => {
    handleMenuClose();
    openSyncDialog();
  };

  const fftApply = (fftZoom, fftSize, variant) => {
    closeEditorDAWFineTuneDialog();
    const options = {
      fftSamples: fftSize,
      spectrogramVariant: variant,
      fftFrequencies: { min: fftZoom[0], max: fftZoom[1] },
    };
    daw?.setTrackFrequencyRange(
      trackId,
      options.fftFrequencies.min,
      options.fftFrequencies.max,
    );
    daw?.setTrackSpectrogramVariant(trackId, variant);
    updatePreferences(options);
  };

  useEffect(() => {
    if (daw && activeRegionGroup && activeRegionGroup?.regions.length > 0) {
      selectTag(activeRegionGroup.name);
    }
  }, [daw, activeRegionGroup, selectTag]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <>
      <Menu
        id="basic-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleMenuClose}
        closeAfterTransition={false}
        slotProps={{ list: { "aria-labelledby": "basic-button" } }}
      >
        {!readOnly && (
          <Tooltip
            placement="left"
            arrow
            title={
              createModelDisabled &&
              "Annotate and select reference annotation to enable"
            }
          >
            <span>
              <MenuItem
                disabled={createModelDisabled || isNoiseFiltering}
                onClick={handleCreateModel}
              >
                Create Model
              </MenuItem>
            </span>
          </Tooltip>
        )}
        <Tooltip
          placement="left"
          arrow
          title={glimpseDisabled && "Must be multichannel"}
        >
          <span>
            <MenuItem
              disabled={glimpseDisabled || isNoiseFiltering}
              onClick={handleGlimpseClick}
            >
              Focus (GLIMPSE)
            </MenuItem>
          </span>
        </Tooltip>
        <MenuItem disabled={isNoiseFiltering} onClick={normalizeFilter}>
          Normalize
        </MenuItem>
        {useSync && (
          <Tooltip
            placement="left"
            arrow
            title={
              syncDisabled &&
              "Provide at least two active channels, annotate, and have alignment reference annotation selected to enable"
            }
          >
            <span>
              <MenuItem
                disabled={syncDisabled || isNoiseFiltering}
                onClick={handleSyncClick}
              >
                Align & Combine
              </MenuItem>
            </span>
          </Tooltip>
        )}
        <Divider sx={{ my: 1 }} />
        <Tooltip
          placement="left"
          arrow
          title={
            sngDisabled &&
            "Delete all but one channel, annotate, and select a reference annotation to enable"
          }
        >
          <span>
            <MenuItem
              disabled={sngDisabled}
              onClick={handleSngShowDialogNEWClick}
            >
              Noise Gate (Spectral)
            </MenuItem>
          </span>
        </Tooltip>
        <Tooltip
          placement="left"
          arrow
          title={monoFilterDisabled && "Delete all but one channel to enable"}
        >
          <span>
            <MenuItem
              disabled={monoFilterDisabled || isNoiseFiltering}
              onClick={handleAsiClick}
            >
              ASI Filter
            </MenuItem>
          </span>
        </Tooltip>
        <Tooltip
          placement="left"
          arrow
          title={monoFilterDisabled && "Delete all but one channel to enable"}
        >
          <span>
            <MenuItem
              disabled={monoFilterDisabled || isNoiseFiltering}
              onClick={handleNlmsClick}
            >
              NLMS Filter
            </MenuItem>
          </span>
        </Tooltip>
      </Menu>
      <Paper
        elevation={4}
        sx={{
          display: "flex",
          flexDirection: "column",
          my: 1,
          px: 4,
          py: 0,
          position: "relative",
          zIndex: 150,
        }}
      >
        <Box
          display="flex"
          height={40}
          justifyContent="space-between"
          alignItems="center"
          width={1}
        >
          {!isLimited ? (
            <Box display="flex" gap={0.5} width={0.1}>
              {!readOnly && (
                <Tooltip title="Trim Content">
                  <span>
                    <IconButton
                      disabled={
                        isSaving || !canPerformOperationsOnSelection || !daw
                      }
                      onClick={handleTrimClick}
                    >
                      <ContentCut
                        color={
                          isSaving || !canPerformOperationsOnSelection || !daw
                            ? "disabled"
                            : "primary"
                        }
                      />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
            </Box>
          ) : (
            <Box display="flex" gap={0.5} width={0.1} />
          )}
          <Box display="flex" gap={2} width={0.8}>
            <Box display="flex" flex={1} justifyContent="flex-end">
              <EditorDAWTimeDisplay
                label="Clip Time"
                value={currentTime.toFixed(3)}
              />
            </Box>
            <Box display="flex" flex={1} justifyContent="flex-start">
              <EditorDAWTimeDisplay
                label="Duration"
                value={duration.toFixed(3)}
              />
            </Box>
          </Box>
          <Box display="flex" justifyContent="flex-end" gap={0.5} width={0.1}>
            {!readOnly && (
              <>
                <Tooltip title="FFT Settings">
                  <span>
                    <IconButton
                      aria-label="FFT"
                      onClick={() => openEditorDAWFineTuneDialog()}
                    >
                      <Tune
                        color={
                          isSaving || surferLoading ? "disabled" : "primary"
                        }
                      />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Filters">
                  <span>
                    <IconButton
                      aria-label="Filters"
                      disabled={isSaving || surferLoading}
                      onClick={handleMenuClick}
                    >
                      <FilterAlt
                        color={
                          isSaving || surferLoading ? "disabled" : "primary"
                        }
                      />
                    </IconButton>
                  </span>
                </Tooltip>
              </>
            )}
          </Box>
        </Box>
        <Box
          bottom={-1}
          left="50%"
          position="absolute"
          width={0.975}
          sx={{
            transform: "translateX(-50%)",
          }}
        >
          {working && <LinearProgress />}
        </Box>
      </Paper>
      <ASIFilterDialog onRun={runAsiFilter} sampleRate={sampleRate} />
      <ExtractDialog
        lastValue={
          activeRegionGroup?.regions?.length > 0
            ? activeRegionGroupName
            : regions.filter((item) => Boolean(item.tag))[0]?.tag
        }
        onCreate={mergeRegion}
      />
      <EditorDAWFineTuneDialog
        fftOptionsDefault={fftOptionsDefault}
        fftSizeOptions={fftSizeOptions}
        maxFrequency={sampleRate / 2.0}
        spectrogramVariant={spectrogramVariant}
        zoom={[fftFrequencies.min, fftFrequencies.max]}
        onApply={fftApply}
        onCancel={handleFineTuneCancel}
      />
      <NLMSFilterDialog onRun={runNlmsFilter} sampleRate={sampleRate} />
      <SingleChannelActiveDialog filterName={filterName} />
      <SngSettingDialogNEW onRun={runSngFilter} />
      <SyncDialog
        clipLength={clipLength}
        sampleRate={sampleRate}
        numberOfChannels={numberOfChannels}
        onRun={runSync}
      />
      <SyncSelectedDialog
        onSyncSelectedClose={handleSyncSelectedClose}
        onSyncSelectedContinue={handleSyncSelectedContinue}
      />
      <GlimpseAlert />
      <SngAlert regions={regions} selectedTag={selectedTag} />
      <SyncAlert regions={regions} selectedTag={selectedTag} />
    </>
  );
};
